// icons.tsx — small inline SVG icon set for the drawer nav (no icon library dependency,
// per the "dependency-light" quality bar). All stroke=currentColor so they inherit the
// nav button's text color (muted by default, --accent when active) — no new hues.
import type { FunctionComponent } from 'preact';
import type { View } from '../lib/router.js';

const COMMON = {
  width: 22,
  height: 22,
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  'stroke-width': 1.8,
  'stroke-linecap': 'round' as const,
  'stroke-linejoin': 'round' as const,
  'aria-hidden': true,
};

function CockpitIcon() {
  return (
    <svg {...COMMON}>
      <rect x="3" y="3" width="7.5" height="7.5" rx="1.5" />
      <rect x="13.5" y="3" width="7.5" height="7.5" rx="1.5" />
      <rect x="3" y="13.5" width="7.5" height="7.5" rx="1.5" />
      <rect x="13.5" y="13.5" width="7.5" height="7.5" rx="1.5" />
    </svg>
  );
}

function VergleichIcon() {
  return (
    <svg {...COMMON}>
      <path d="M6 20V10M12 20V4M18 20V14" />
    </svg>
  );
}

function SzenarienIcon() {
  return (
    <svg {...COMMON}>
      <path d="M5 4h9l5 5v11a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V5a1 1 0 0 1 1-1z" />
      <path d="M14 4v5h5" />
      <path d="M8 13h8M8 17h5" />
    </svg>
  );
}

function HistorieIcon() {
  return (
    <svg {...COMMON}>
      <path d="M4 19V5" />
      <path d="M4 19h16" />
      <path d="M6.5 15.5l4-4.5 3 3 5-6" />
    </svg>
  );
}

function EinstellungenIcon() {
  return (
    <svg {...COMMON}>
      <circle cx="12" cy="12" r="3.2" />
      <path d="M12 3v3.2M12 17.8V21M3 12h3.2M17.8 12H21M5.8 5.8l2.3 2.3M15.9 15.9l2.3 2.3M18.2 5.8l-2.3 2.3M8.1 15.9l-2.3 2.3" />
    </svg>
  );
}

function InfoIcon() {
  return (
    <svg {...COMMON}>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 11v6" />
      <path d="M12 7.2v.01" />
    </svg>
  );
}

function DatenIcon() {
  return (
    <svg {...COMMON}>
      <ellipse cx="12" cy="6" rx="7" ry="3" />
      <path d="M5 6v6c0 1.66 3.13 3 7 3s7-1.34 7-3V6" />
      <path d="M5 12v6c0 1.66 3.13 3 7 3s7-1.34 7-3v-6" />
    </svg>
  );
}

const ICONS: Record<View, FunctionComponent> = {
  cockpit: CockpitIcon,
  vergleich: VergleichIcon,
  szenarien: SzenarienIcon,
  historie: HistorieIcon,
  einstellungen: EinstellungenIcon,
  info: InfoIcon,
  daten: DatenIcon,
};

export function NavIcon({ name }: { name: View }) {
  const Cmp = ICONS[name];
  return <Cmp />;
}

export function BurgerIcon() {
  return (
    <svg {...COMMON} width={24} height={24}>
      <path d="M4 6h16M4 12h16M4 18h16" />
    </svg>
  );
}

export function CloseIcon() {
  return (
    <svg {...COMMON} width={22} height={22}>
      <path d="M6 6l12 12M18 6L6 18" />
    </svg>
  );
}

/** The Erklärungen (document-by-design) toggle's icon — app bar, drawer and sidebar all
 * share this same "ⓘ" glyph (components/Hint.tsx owns the actual on/off state). Distinct
 * function from the nav's own InfoIcon (Info & Steuer page) even though the shape is
 * identical — this one is never part of the View-keyed ICONS map. */
export function ExplainIcon() {
  return (
    <svg {...COMMON} width={20} height={20}>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 11v6" />
      <path d="M12 7.2v.01" />
    </svg>
  );
}

/** "⋯" kebab — the scenario switcher's action menu trigger (Umbenennen/Duplizieren/Auf
 * Standard zurücksetzen/Löschen), see ScenarioSwitcher.tsx + components/PopoverMenu.tsx.
 * Filled dots (not stroked, unlike the rest of this file's outline style) reads more
 * clearly as "more actions" at the small size a toolbar icon button needs. */
export function MoreIcon() {
  return (
    <svg width={18} height={18} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <circle cx="5" cy="12" r="2" />
      <circle cx="12" cy="12" r="2" />
      <circle cx="19" cy="12" r="2" />
    </svg>
  );
}

/** Pencil — the Vergleich comparison table's per-column "im Cockpit bearbeiten"
 * affordance (VergleichView.tsx). */
export function EditIcon() {
  return (
    <svg {...COMMON} width={16} height={16}>
      <path d="M4 20l0.9-3.6L16.4 4.9a1.5 1.5 0 0 1 2.1 0l0.6 0.6a1.5 1.5 0 0 1 0 2.1L7.6 19.1 4 20z" />
      <path d="M14.5 6.8l2.7 2.7" />
    </svg>
  );
}
